/**
 * @author Erik Haßlmeyer <erik.hasslmeyer@refinio.net>
 * @copyright REFINIO GmbH 2022
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { symmetricDecrypt, symmetricDecryptWithEmbeddedNonce, symmetricEncrypt, symmetricEncryptAndEmbedNonce } from './encryption.js';
/**
 * Class provides functions for symmetric encryption and decryption.
 */
export class SymmetricCryptoApi {
    #symmetricKey;
    constructor(symmetricKey) {
        this.#symmetricKey = symmetricKey;
    }
    /**
     * Same as encryption.ts:symmetricEncrypt
     *
     * @param {Uint8Array | ArrayBufferLike} data
     * @param {Nonce} nonce
     * @returns {Uint8Array}
     */
    encrypt(data, nonce) {
        return symmetricEncrypt(data, this.#symmetricKey, nonce);
    }
    /**
     * Same as encryption.ts:symmetricEncryptAndEmbedNonce
     *
     * @param {Uint8Array | ArrayBufferLike} data
     * @param {Nonce} nonce
     * @returns {Uint8Array}
     */
    encryptAndEmbedNonce(data, nonce) {
        return symmetricEncryptAndEmbedNonce(data, this.#symmetricKey, nonce);
    }
    /**
     * Same as encryption.ts:symmetricDecrypt
     *
     * @param {Uint8Array | ArrayBufferLike} cypher
     * @param {Nonce} nonce
     * @returns {Uint8Array}
     */
    decrypt(cypher, nonce) {
        return symmetricDecrypt(cypher, this.#symmetricKey, nonce);
    }
    /**
     * Same as encryption.ts:symmetricDecryptAndRemoveNonce
     *
     * @param {Uint8Array | ArrayBufferLike} cypherAndNonce
     * @returns {Uint8Array}
     */
    decryptWithEmbeddedNonce(cypherAndNonce) {
        return symmetricDecryptWithEmbeddedNonce(cypherAndNonce, this.#symmetricKey);
    }
}
/**
 * Stores the keys that were used to derive the symmetric crypto API within this object.
 *
 * This convenience wrapper makes many interfaces much easier, because they don't need to pass
 * around the keys all the time.
 */
export class SymmetricCryptoApiWithKeys extends SymmetricCryptoApi {
    localPublicKey;
    remotePublicKey;
    constructor(symmetricKey, localPublicKey, remotePublicKey) {
        super(symmetricKey);
        this.localPublicKey = localPublicKey;
        this.remotePublicKey = remotePublicKey;
    }
}
//# sourceMappingURL=SymmetricCryptoApi.js.map